// The outline of a long article (core outline.ts finds its sections): a quiet list in the
// reader's right margin when there is room beside the text, otherwise a Contents button on the
// reader bar. It marks the section being read and jumps to any other, with Back to return.
import { Component, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode, type RefObject } from "react";
import { createPortal } from "react-dom";
import { currentSection, jumpTo, worthOutline, type Section } from "@vladrss/core";
import { Icon } from "./Icon";
import { toast } from "../lib/store";
import type { Entry } from "./Discussion";

/** The margin list's width, and the space it keeps from the text and the window's edge. */
const LIST = 216, GAP = 40, EDGE = 20;
/** The least space kept to the left of the text when the column moves over to make room. */
const LEFT_MIN = 56;

type Place = { mode: "off" } | { mode: "bar" } | { mode: "margin"; shift: number; width: number; right: number };

/**
 * Where the outline goes for this article and window: nowhere (short, or too few sections), in
 * the margin (moving the text column left a little when that makes room), or on the bar.
 */
function place(sections: readonly Section[], scroller: HTMLElement, article: HTMLElement): Place {
  const prose = article.querySelector(".prose");
  const height = prose ? prose.getBoundingClientRect().bottom - article.getBoundingClientRect().top : 0;
  if (!worthOutline(sections, height, scroller.clientHeight)) return { mode: "off" };
  const pad = parseFloat(getComputedStyle(article).paddingLeft) || 0;
  const textLeft = article.offsetLeft + pad, textRight = article.offsetLeft + article.offsetWidth - pad;
  const bar = scroller.offsetWidth - scroller.clientWidth; // the scrollbar's own width
  const space = scroller.clientWidth - textRight - EDGE;
  const need = LIST + GAP;
  const shift = Math.max(0, Math.min(need - space, textLeft - LEFT_MIN));
  if (space + shift < need) return { mode: "bar" };
  return { mode: "margin", shift, width: Math.min(260, space + shift - GAP), right: bar + EDGE };
}

/**
 * The outline for the open article. [sections] come from the article's body once it is shaped;
 * [slot] is the place on the reader bar for the Contents button. [after] is the discussion, listed
 * last when there is one: it never decides whether the article earns an outline.
 */
export function Outline({ sections: found, scroller, article, slot, after }: {
  sections: Section[]; scroller: RefObject<HTMLDivElement | null>; article: RefObject<HTMLElement | null>; slot: HTMLElement | null; after?: Entry | null;
}) {
  const [at, setAt] = useState<Place>({ mode: "off" });
  const [tracked, setCurrent] = useState(0);
  const sections = useMemo<Entry[]>(() => (after && found.length ? [...found, after] : found), [found, after]);
  // a new article's sections start from the top, whatever the last one reached
  const current = Math.max(0, Math.min(tracked, sections.length - 1));
  useLayoutEffect(() => setCurrent(0), [found]);
  const [open, setOpen] = useState(false);
  const list = useRef<HTMLElement>(null);

  // where it goes: again whenever the window, the text or its photos change size
  useLayoutEffect(() => {
    const sc = scroller.current, ar = article.current;
    if (!sc || !ar || !found.length) { setAt({ mode: "off" }); return; }
    const measure = () => setAt((old) => {
      // measured with the column where it would be without the outline
      ar.style.left = "";
      const next = place(found, sc, ar);
      ar.style.left = next.mode === "margin" && next.shift ? `${-next.shift}px` : "";
      return JSON.stringify(old) === JSON.stringify(next) ? old : next;
    });
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(sc); ro.observe(ar);
    return () => { ro.disconnect(); ar.style.left = ""; };
  }, [found, scroller, article]);

  // the section being read, as the page scrolls
  useEffect(() => {
    const sc = scroller.current;
    if (!sc || at.mode === "off") return;
    let frame = 0;
    const track = () => { frame = 0; setCurrent(currentSection(sections, sc)); };
    const onScroll = () => { if (!frame) frame = requestAnimationFrame(track); };
    track();
    sc.addEventListener("scroll", onScroll, { passive: true });
    return () => { sc.removeEventListener("scroll", onScroll); cancelAnimationFrame(frame); };
  }, [sections, scroller, at.mode]);

  // a long list keeps the current section in view
  useEffect(() => { list.current?.querySelector<HTMLElement>("[aria-current]")?.scrollIntoView({ block: "nearest" }); }, [current]);

  const jump = (i: number) => {
    const sc = scroller.current, s = sections[i];
    if (!sc || !s) return;
    setOpen(false);
    s.open?.();
    const from = jumpTo(sc, s.el);
    // a jump of more than a screen offers the way back
    if (Math.abs(sc.scrollTop - from) > sc.clientHeight || Math.abs(s.el.getBoundingClientRect().top) > sc.clientHeight) {
      toast(`Jumped to ${s.label}`, () => jumpTo(sc, from), "contents", "Back");
    }
  };

  // n and p: the next and previous section
  useEffect(() => {
    if (at.mode === "off") return;
    const step = (e: Event) => {
      const d = (e as CustomEvent<number>).detail;
      const sc = scroller.current;
      if (!sc) return;
      const here = currentSection(sections, sc);
      // the next section is the next one below the reading line, even mid-way through this one
      jump(Math.max(0, Math.min(sections.length - 1, here + d)));
    };
    addEventListener("vladrss:section", step);
    return () => removeEventListener("vladrss:section", step);
  });

  if (at.mode === "off") return null;
  // sub-sections show under the section they belong to, while it is being read
  const group = (i: number) => { for (let j = i; j >= 0; j--) if (sections[j]?.level === 1) return j; return 0; };
  const here = group(current);
  const items = sections.map((s, i) => ({ s, i })).filter(({ s, i }) => s.level === 1 || group(i) === here);
  const entries = (onPick: (i: number) => void) => items.map(({ s, i }) => (
    <a key={i} href="#" className={`${s.level === 2 ? "sub" : ""}${i < current ? " past" : ""}`} aria-current={i === current ? "location" : undefined}
      title={s.label.length > 48 ? s.label : undefined} onClick={(e) => { e.preventDefault(); onPick(i); }}>{s.label}</a>
  ));

  if (at.mode === "margin") {
    return (
      <nav className="toc" aria-label="Contents" ref={list} style={{ width: at.width, right: at.right }}>
        <span className="toc-h">Contents</span>
        {entries(jump)}
      </nav>
    );
  }
  return slot && createPortal(
    <ContentsButton open={open} setOpen={setOpen} label={sections[current]?.label ?? ""}>
      <nav className="menu tocpop" aria-label="Contents" ref={list}>{entries(jump)}</nav>
    </ContentsButton>,
    slot,
  );
}

/** A part reading can do without: if it ever fails it is simply not shown, and the article stays open. */
export class Optional extends Component<{ children: ReactNode }, { failed: boolean }> {
  state = { failed: false };
  static getDerivedStateFromError() { return { failed: true }; }
  componentDidCatch(e: unknown) { console.error(e); }
  render() { return this.state.failed ? null : this.props.children; }
}

/** The reader bar's Contents button, for a reader too narrow for the margin list. */
function ContentsButton({ open, setOpen, label, children }: { open: boolean; setOpen: (v: boolean) => void; label: string; children: ReactNode }) {
  const wrap = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const away = (e: PointerEvent) => { if (!wrap.current?.contains(e.target as Node)) setOpen(false); };
    const esc = (e: KeyboardEvent) => { if (e.key === "Escape") { e.stopPropagation(); setOpen(false); } };
    addEventListener("pointerdown", away); addEventListener("keydown", esc, true);
    return () => { removeEventListener("pointerdown", away); removeEventListener("keydown", esc, true); };
  }, [open, setOpen]);
  return (
    <div className="more-wrap" ref={wrap}>
      <button className={`ib tb${open ? " open" : ""}`} onClick={() => setOpen(!open)} aria-haspopup="dialog" aria-expanded={open} aria-label={`Contents: ${label}`} data-tip={label ? `Contents · ${label}` : "Contents"} data-key="N">
        <Icon name="contents" /><span className="tl">Contents</span>
      </button>
      {open && children}
    </div>
  );
}
